/* eslint-disable react/prop-types -- props validated via TypeScript StepProps */
import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { Box, Typography, Button, Paper, CircularProgress, useTheme } from '@mui/material';
import { FaChevronRight } from 'react-icons/fa';
import Input from '@/components/Common/Input';

export const REASONS = [
  'Too many missed calls',
  'After-hour call coverage',
  'Lunch break call coverage',
  'Reduce front desk workload',
  'Improve patient/customer experience',
  'Cost savings over hiring staff',
  'Competitor already using AI',
  'Scale without adding headcount',
  'Spam call filtering',
  'Other'
];

interface StepProps {
  onNext: () => void | Promise<void>;
  control: Control<any>;
  isSubmitting?: boolean;
  billingEnabled?: boolean;
}

const AIReceptionistReasonsStep: React.FC<StepProps> = ({
  onNext,
  control,
  isSubmitting = false,
  billingEnabled = true
}) => {
  const theme = useTheme();

  const chipSx = {
    px: 3,
    py: 1.2,
    borderRadius: '30px',
    border: '1px solid #e5e7eb',
    cursor: 'pointer',
    background: '#fff',
    fontSize: '13px',
    fontWeight: 500,
    transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease'
  } as const;

  const chipSelectedSx = {
    background: theme.palette.primary.main,
    color: '#fff',
    borderColor: theme.palette.primary.main
  } as const;
  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        borderRadius: 4,
        width: '100%',
        border: '1px solid #f1f5f9',
        boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.05), 0 8px 10px -6px rgb(0 0 0 / 0.05)'
      }}
    >
      <Typography fontWeight={600} sx={{ fontSize: { xs: '16px', md: '24px' } }}>
        Why are you choosing an AI Receptionist?
      </Typography>

      <Typography color='text.secondary' fontSize='14px' mt={1} lineHeight='normal'>
        Select all the reasons that apply. This helps us tailor your AI agent to solve your biggest pain points.
      </Typography>

      <Controller
        name='ai_receptionist_reasons'
        control={control}
        rules={{ validate: (val) => (val && val.length > 0) || 'Please select at least one reason' }}
        render={({ field, fieldState }) => {
          const selectedValues = field.value || [];

          const toggleReason = (reason: string) => {
            if (selectedValues.includes(reason)) {
              field.onChange(selectedValues.filter((r: string) => r !== reason));
            } else {
              field.onChange([...selectedValues, reason]);
            }
          };
          return (
            <>
              <Box display='flex' gap={2} flexWrap='wrap' mt={3}>
                {REASONS.map((reason) => {
                  const isSelected = selectedValues.includes(reason);
                  return (
                    <Box
                      key={reason}
                      component='button'
                      type='button'
                      onClick={() => toggleReason(reason)}
                      sx={{
                        ...chipSx,
                        ...(isSelected ? chipSelectedSx : {})
                      }}
                    >
                      {reason}
                    </Box>
                  );
                })}
              </Box>

              {selectedValues.includes('Other') && (
                <Box mt={3}>
                  <Input
                    name='other_ai_receptionist_reason'
                    control={control}
                    label='Please specify your reason'
                    placeholder='Type your reason here...'
                    fullWidth
                    autoFocus
                  />
                </Box>
              )}

              {fieldState.error && (
                <Typography color='error' fontSize='12px' mt={2} fontWeight={500}>
                  {fieldState.error.message}
                </Typography>
              )}
            </>
          );
        }}
      />

      <Button
        sx={{
          background: theme.palette.primary.main,
          mt: 3,
          gap: 1,
          display: { xs: 'none', md: 'flex' }
        }}
        variant='contained'
        fullWidth
        disabled={isSubmitting}
        onClick={onNext}
      >
        {isSubmitting ? (
          <CircularProgress size={24} color='inherit' />
        ) : (
          <>
            {billingEnabled ? 'Continue to Payment' : 'Continue'} <FaChevronRight size={14} />
          </>
        )}
      </Button>
    </Paper>
  );
};

export default AIReceptionistReasonsStep;
